// src/pages/Users/Galleries/RemovePhotoFromAlbumModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FaTrash, FaTimes } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import {
  getPhotosByGalleryId,
  deletePhoto,
} from "../../../services/photoServices";
import { toast } from "react-toastify";

// ✅ helper pour rendre les URLs /uploads/... absolues vers l’API
import { toAbsolutePhotoUrl as toAbsPhoto } from "../../../services/albumPhotoServices";

const FALLBACK = "/img/no-image.jpg";

function stripHtml(html = "") {
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").trim();
}

export default function RemovePhotoFromAlbumModal({
  isOpen,
  onClose,
  galleryId,
}) {
  // Etat UI
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [confirm, setConfirm] = useState(false);

  // Données
  const [photos, setPhotos] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");

  // Refs / UX
  const selectRef = useRef(null);

  // Lock scroll + ESC + focus
  useEffect(() => {
    if (!isOpen) return;
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);

    const t = setTimeout(() => selectRef.current?.focus(), 50);

    return () => {
      root.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [isOpen, onClose]);

  // Fetch photos à l’ouverture / changement d’album
  useEffect(() => {
    if (!isOpen || !galleryId) return;
    (async () => {
      setLoading(true);
      try {
        const data = await getPhotosByGalleryId(galleryId);
        setPhotos(Array.isArray(data) ? data : []);
        if (Array.isArray(data) && data.length > 0) {
          setSelectedId(String(data[0].id));
        } else {
          setSelectedId("");
        }
      } catch (err) {
        console.error("Failed to fetch photos:", err);
        toast.error("Impossible de charger les photos.");
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, galleryId]);

  // Filtrage par recherche dans la légende
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return photos;
    return photos.filter((p) =>
      stripHtml(p.description || "")
        .toLowerCase()
        .includes(q)
    );
  }, [photos, search]);

  // Photo sélectionnée
  const selected = useMemo(
    () => filtered.find((p) => String(p.id) === String(selectedId)) || null,
    [filtered, selectedId]
  );

  const previewUrl = toAbsPhoto(selected?.photo_url || "");
  const previewCaption = stripHtml(selected?.description || "");

  const onImgError = (e) => {
    if (e?.currentTarget) e.currentTarget.src = FALLBACK;
  };

  const canDelete = !!selected && confirm && !deleting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected) {
      toast.warn("Choisis une photo à supprimer.");
      return;
    }
    if (!confirm) {
      toast.warn("Coche la confirmation pour supprimer.");
      return;
    }

    try {
      setDeleting(true);
      await deletePhoto(Number(selected.id));
      toast.success("Photo supprimée avec succès.");
      onClose?.();
    } catch (error) {
      console.error("Erreur lors de la suppression de la photo:", error);
      toast.error("Erreur lors de la suppression de la photo.");
    } finally {
      setDeleting(false);
    }
  };

  // Render (Portal)
  return isOpen
    ? ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-3 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Supprimer une photo"
          onClick={(e) => e.currentTarget === e.target && onClose?.()}
        >
          <div
            className="w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-rose-600 via-pink-600 to-orange-500 text-white flex items-center justify-between">
              <h3 className="font-semibold">Supprimer une photo de l’album</h3>
              <button
                onClick={onClose}
                title="Fermer"
                aria-label="Fermer"
                className="p-2 rounded-full hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70"
              >
                <FaTimes />
              </button>
            </div>

            {/* Body */}
            <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
              {/* Recherche */}
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr,220px]">
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Rechercher par légende
                  </label>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Tape quelques mots…"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Sélectionne la photo
                  </label>
                  <select
                    ref={selectRef}
                    value={selectedId}
                    onChange={(e) => setSelectedId(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  >
                    {filtered.map((p, i) => {
                      const label =
                        stripHtml(p.description || "") ||
                        `Photo #${p.id || i + 1}`;
                      return (
                        <option key={p.id} value={p.id}>
                          {label.length > 80 ? label.slice(0, 80) + "…" : label}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              {/* Etat de chargement / vide */}
              {loading && (
                <div className="rounded-xl bg-slate-200/70 dark:bg-slate-800/60 h-40 animate-pulse" />
              )}
              {!loading && filtered.length === 0 && (
                <div className="text-center py-10 text-slate-600 dark:text-slate-300">
                  Aucune photo trouvée pour cet album.
                </div>
              )}

              {/* Aperçu */}
              {!loading && selected && (
                <div className="rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-800 bg-slate-50 dark:bg-slate-900">
                  <div className="w-full" style={{ aspectRatio: "16 / 9" }}>
                    <img
                      src={previewUrl || FALLBACK}
                      alt={previewCaption || "Aperçu"}
                      className="w-full h-full object-contain bg-slate-50 dark:bg-slate-800"
                      onError={onImgError}
                    />
                  </div>
                  {previewCaption && (
                    <div className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
                      {previewCaption}
                    </div>
                  )}
                </div>
              )}

              {/* Confirmation */}
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={confirm}
                  onChange={(e) => setConfirm(e.target.checked)}
                  className="accent-rose-600"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  Je confirme supprimer définitivement cette photo.
                </span>
              </label>

              {/* Actions */}
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  text="Annuler"
                  onClick={onClose}
                  icon={FaTimes}
                  type="button"
                  className="gap-2 rounded-xl"
                />
                <Button
                  text={deleting ? "Suppression…" : "Supprimer"}
                  icon={FaTrash}
                  type="submit"
                  className="gap-2 rounded-xl"
                  disabled={!canDelete}
                />
              </div>
            </form>
          </div>
        </div>,
        document.body
      )
    : null;
}
